import { useCallback, useMemo } from 'react';
import useSWR from 'swr';
import { configApi } from 'src/api/configApi';
import {
  PACKAGE_QUOTAS,
  PR_PACKAGES,
  normalizeCompanyName,
  getCompanyData as getCompanyDataUtil,
  getCompanyQuotas as getCompanyQuotasUtil,
  getCompanyPackage as getCompanyPackageUtil,
  getPrimaryLeadId as getPrimaryLeadIdUtil,
  getPrimaryLeadName as getPrimaryLeadNameUtil,
} from 'src/utils/collections/companyLeads';

const CONFIG_KEY = 'companyData';

/**
 * Hook for managing company data with backend persistence via configApi
 * Data is stored entirely in the backend - no local defaults
 */
const useCompanyData = () => {
  const {
    data: backendData,
    error,
    isLoading,
    mutate,
  } = useSWR(CONFIG_KEY, async () => {
    try {
      const response = await configApi.fetchConfigs(CONFIG_KEY);
      // Response could be null/undefined if config doesn't exist yet
      if (response?.value) {
        return JSON.parse(response.value);
      }
      return {};
    } catch {
      // Error fetching config - return empty object
      return {};
    }
  });

  // Use backend data directly (empty object if not loaded yet)
  const companyData = useMemo(() => backendData || {}, [backendData]);

  /**
   * Update a single company's data
   * @param {string} companyKey - The normalized company key (kebab-case)
   * @param {object} newData - The new data for the company { leadId, package, quotas? }
   */
  const updateCompanyData = useCallback(
    async (companyKey, newData) => {
      // Update local data
      const updated = { ...companyData, [companyKey]: newData };

      // Update backend
      await configApi.updateConfig(CONFIG_KEY, JSON.stringify(updated));

      // Revalidate from server
      mutate();
    },
    [companyData, mutate]
  );

  /**
   * Hide a company from the dashboard by setting a hidden flag
   * @param {string} companyKey - The normalized company key (kebab-case)
   */
  const removeCompanyData = useCallback(
    async (companyKey) => {
      const existing = companyData[companyKey] || {};
      const updated = { ...companyData, [companyKey]: { ...existing, hidden: true } };

      // Update backend with the company marked as hidden
      await configApi.updateConfig(CONFIG_KEY, JSON.stringify(updated));

      // Revalidate from server
      mutate();
    },
    [companyData, mutate]
  );

  /**
   * Restore a previously hidden company
   * @param {string} companyKey - The normalized company key (kebab-case)
   */
  const restoreCompanyData = useCallback(
    async (companyKey) => {
      const existing = companyData[companyKey];
      if (!existing) return;
      const rest = { ...existing };
      delete rest.hidden;
      const updated = { ...companyData, [companyKey]: rest };

      await configApi.updateConfig(CONFIG_KEY, JSON.stringify(updated));
      mutate();
    },
    [companyData, mutate]
  );

  /**
   * Check if a company is hidden
   * @param {string} companyName - The company name (any casing)
   * @returns {boolean}
   */
  const isCompanyHidden = useCallback(
    (companyName) => {
      const key = normalizeCompanyName(companyName);
      return companyData[key]?.hidden === true;
    },
    [companyData]
  );

  // Helper functions that use the merged company data
  const getCompanyData = useCallback(
    (companyName) => getCompanyDataUtil(companyName, companyData),
    [companyData]
  );

  const getCompanyQuotas = useCallback(
    (companyName) => getCompanyQuotasUtil(companyName, companyData),
    [companyData]
  );

  const getCompanyPackage = useCallback(
    (companyName) => getCompanyPackageUtil(companyName, companyData),
    [companyData]
  );

  const getPrimaryLeadId = useCallback(
    (companyName) => getPrimaryLeadIdUtil(companyName, companyData),
    [companyData]
  );

  const getPrimaryLeadName = useCallback(
    (companyName, users = []) => getPrimaryLeadNameUtil(companyName, users, companyData),
    [companyData]
  );

  const getCompanyStatus = useCallback(
    (companyName) => {
      const key = normalizeCompanyName(companyName);
      return companyData[key]?.status || null;
    },
    [companyData]
  );

  return {
    // Raw data
    companyData,
    isLoading,
    error,

    // Mutation functions
    updateCompanyData,
    removeCompanyData,
    restoreCompanyData,
    mutate,

    // Query functions
    isCompanyHidden,

    // Helper functions (use backend data)
    getCompanyData,
    getCompanyQuotas,
    getCompanyPackage,
    getPrimaryLeadId,
    getPrimaryLeadName,
    getCompanyStatus,

    // Re-export utilities for convenience
    normalizeCompanyName,
    PACKAGE_QUOTAS,
    PR_PACKAGES,
  };
};

export default useCompanyData;
